import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Drag-and-drop sortable AntD Table (per antd.design/components/table#drag-sorting).
 *
 * Wraps AntD Table with @dnd-kit — same visual as a normal Table plus a drag
 * handle column at the start. The caller supplies `dataSource`, `columns`,
 * and an `onReorder(newOrderIds)` async callback. We manage optimistic UI:
 *   - drop → local state updates immediately
 *   - onReorder awaited; on error → rollback + error toast, on success → toast
 *
 * Keyboard: focus a drag handle, Space to grab, arrows to move, Space to drop.
 * Touch: default press-to-drag activation. All handled by dnd-kit's sensors.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Table } from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import { DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy, } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { notifySuccess } from './errors';
/**
 * Table row wired to useSortable. dnd-kit's `attributes` + `listeners` are
 * exposed via React context so the DragHandle cell down the tree can attach
 * them to its icon (drag handle limited to the icon, not the whole row).
 */
// Deliberately loosen the types — dnd-kit's SyntheticListenerMap and
// DraggableAttributes both spread cleanly onto a native element, but their
// declared types don't unify with React.HTMLAttributes. Using `any` here
// keeps callers strict and hides one library-boundary detail.
const RowContext = React.createContext({ attributes: null, listeners: null });
function SortableRow(props) {
    const { setNodeRef, transform, transition, isDragging, attributes, listeners } = useSortable({ id: props['data-row-key'] });
    const style = {
        ...props.style,
        transform: CSS.Translate.toString(transform),
        transition,
        ...(isDragging ? { position: 'relative', zIndex: 9999, background: '#e6f4ff' } : {}),
    };
    return (_jsx(RowContext.Provider, { value: { attributes, listeners: listeners ?? null }, children: _jsx("tr", { ...props, ref: setNodeRef, style: style }) }));
}
/**
 * Drag-handle cell. Accessible: renders a real button so screen readers /
 * keyboard users get proper focus + Space-to-grab semantics from dnd-kit.
 */
function DragHandle() {
    const { attributes, listeners } = React.useContext(RowContext);
    return (_jsx("button", { type: "button", "aria-label": "\u05D2\u05E8\u05D5\u05E8 \u05DC\u05E1\u05D9\u05D3\u05D5\u05E8", style: {
            cursor: 'grab', touchAction: 'none', border: 0, background: 'transparent',
            color: '#8c8c8c', padding: 4, display: 'inline-flex', alignItems: 'center',
        }, ...(attributes ?? {}), ...(listeners ?? {}), children: _jsx(HolderOutlined, {}) }));
}
export function SortableTable({ dataSource, columns, onReorder, successMessage = 'הסדר נשמר', errorPrefix = 'שמירת הסדר נכשלה', ...tableProps }) {
    // Local mirror — displayed rows. Kept in sync with dataSource unless we're
    // in the middle of an optimistic swap (isReordering).
    const [rows, setRows] = useState(dataSource);
    const [isReordering, setIsReordering] = useState(false);
    useEffect(() => { if (!isReordering)
        setRows(dataSource); }, [dataSource, isReordering]);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
    const enrichedColumns = useMemo(() => (
    // Drag handle is always the first column so the affordance is consistent
    // across every reorderable list.
    [{ title: '', key: '__drag', width: 42, align: 'center', render: () => _jsx(DragHandle, {}) }, ...columns]), [columns]);
    const onDragEnd = async (e) => {
        const { active, over } = e;
        if (!over || active.id === over.id)
            return;
        const prev = rows;
        const from = rows.findIndex((r) => r.id === active.id);
        const to = rows.findIndex((r) => r.id === over.id);
        if (from < 0 || to < 0)
            return;
        const next = arrayMove(rows, from, to);
        // Optimistic UI — reflect the swap before the server confirms.
        setIsReordering(true);
        setRows(next);
        try {
            await onReorder(next.map((r) => r.id));
            notifySuccess(successMessage);
        }
        catch {
            // Roll back to the pre-drop order. The API client's central pipeline
            // has already surfaced the server's Hebrew message as a toast (see
            // apiCall/setErrorReporter); we only need to undo the optimistic
            // reorder here. `errorPrefix` is retained for compatibility but no
            // longer used to build a toast — the plain message is more actionable.
            setRows(prev);
        }
        finally {
            setIsReordering(false);
        }
    };
    return (_jsx(DndContext, { sensors: sensors, modifiers: [restrictToVerticalAxis], onDragEnd: onDragEnd, children: _jsx(SortableContext, { items: rows.map((r) => r.id), strategy: verticalListSortingStrategy, children: _jsx(Table, { rowKey: "id", pagination: false, ...tableProps, dataSource: rows, columns: enrichedColumns, components: { body: { row: SortableRow } } }) }) }));
}
